import { CheckCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface GenerateCodesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function GenerateCodesDialog({ open, onOpenChange, onSuccess }: GenerateCodesDialogProps) {
  const [count, setCount] = useState('50');
  const [generating, setGenerating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [generatedCount, setGeneratedCount] = useState(0);

  const handleGenerate = async () => {
    try {
      setError('');
      setGenerating(true);

      const countNum = parseInt(count, 10);
      if (isNaN(countNum) || countNum < 1 || countNum > 500) {
        setError('Count must be between 1 and 500');
        return;
      }

      const response = await fetch('/api/qr-codes/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ count: countNum }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || data.message || `Failed to generate codes (${response.status})`);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setGeneratedCount(data.data.length);
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          setSuccess(false);
          setCount('50');
        }, 1500);
      } else {
        setError(data.message || 'Failed to generate codes');
      }
    } catch (error) {
      console.error('Error generating codes:', error);
      setError('Failed to generate codes');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    if (!generating) {
      setCount('50');
      setError('');
      setSuccess(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate QR Codes</DialogTitle>
          <DialogDescription>
            Generate a batch of unique QR codes for asset tracking
          </DialogDescription>
        </DialogHeader>

        {!success ? (
          <>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="count">Number of Codes (1-500)</Label>
                <Input
                  id="count"
                  type="number"
                  min="1"
                  max="500"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  disabled={generating}
                />
              </div>

              {error && (
                <div className="text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="text-sm text-muted-foreground">
                Generated codes will be 8-10 characters long and ready for export to CSV
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={generating}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={generating}>
                {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Generate
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="py-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-semibold">
              Successfully generated {generatedCount} QR codes!
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

